import React, { FC, useMemo } from "react";
import moment from "moment";
import { PulseLoader } from "react-spinners";
import CustomTable from "../../../../components/Tables/CustomTable";
import { useAppSelector } from "../../../../redux/app/hooks";
import { PaymentOption } from "../../../../redux/features/paymentOptionsSlice";
import { prettifyPaymentOptionName } from "./utils";

type StatusFilter = "ALL" | "ACTIVE" | "INACTIVE";

interface PaymentMethodsTableProps {
  search: string;
  statusFilter: StatusFilter;
  onToggle: (item: PaymentOption) => void;
}

const initials = (text: string | undefined) =>
  (text ?? "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const isActive = status === "ACTIVE";
  return (
    <span
      className={`text-xs font-semibold px-3 py-1 rounded-full ${
        isActive
          ? "bg-green-100 text-green-700"
          : "bg-gray-100 text-gray-600"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );
};

const ToggleSwitch: FC<{
  checked: boolean;
  loading?: boolean;
  onClick: () => void;
  ariaLabel?: string;
}> = ({ checked, loading, onClick, ariaLabel }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={loading}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
        checked ? "bg-primary" : "bg-gray-300"
      } ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
    {loading && <PulseLoader size={5} color="#3F2EC8" />}
  </div>
);

const PaymentMethodsTable: FC<PaymentMethodsTableProps> = ({
  search,
  statusFilter,
  onToggle,
}) => {
  const { items, loading, toggling } = useAppSelector(
    (state) => state.paymentOptions
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesStatus =
        statusFilter === "ALL" || item.status === statusFilter;
      if (!matchesStatus) return false;
      if (!term) return true;
      const haystack = [item.name, prettifyPaymentOptionName(item.name), item.id]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [items, search, statusFilter]);

  const tableData = {
    columns: [
      { label: "Provider", field: "provider" },
      { label: "Status", field: "status" },
      { label: "Last Updated", field: "updatedAt" },
      { label: "Action", field: "action" },
    ],
    rows: filtered.map((item) => {
      const displayName = prettifyPaymentOptionName(item.name);
      const isActive = item.status === "ACTIVE";
      const isToggling = !!toggling[item.id];
      const updatedAt = item.updatedAt ? moment(item.updatedAt) : null;

      return {
        id: item.id,
        provider: (
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-lightPurple text-accent flex items-center justify-center text-xs font-semibold">
              {initials(displayName)}
            </div>
            <span className="font-medium text-gray-800">{displayName}</span>
          </div>
        ),
        status: <StatusBadge status={String(item.status)} />,
        updatedAt: updatedAt ? (
          <span
            className="text-gray-600"
            title={updatedAt.format("MMM D, YYYY h:mm A")}
          >
            {updatedAt.fromNow()}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
        action: (
          <ToggleSwitch
            checked={isActive}
            loading={isToggling}
            onClick={() => onToggle(item)}
            ariaLabel={`${isActive ? "Disable" : "Enable"} ${displayName}`}
          />
        ),
      };
    }),
  };

  return (
    <CustomTable
      data={tableData}
      loading={loading}
      noRecordText="No payment methods found"
    />
  );
};

export default PaymentMethodsTable;
